import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild, effect, inject } from "@angular/core";
import { RouterLink } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Chart, type ChartConfiguration, registerables } from "chart.js";
import {
  DashboardBadge,
  DashboardProfileResponse,
  SubmissionActivityResponse
} from "../../models/dashboard.models";
import { DashboardService } from "../../services/dashboard.service";
import { AppTheme, ThemeService } from "../../services/theme.service";

Chart.register(...registerables);

type HeatLevel = -1 | 0 | 1 | 2 | 3 | 4;

interface HeatmapCell {
  date: string;
  submissions: number;
  level: HeatLevel;
  inRange: boolean;
}

interface HeatmapMonthLabel {
  label: string;
  column: number;
}

@Component({
  selector: "app-dashboard",
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: "./dashboard.component.html",
  styleUrl: "./dashboard.component.css"
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("ratingChart")
  set ratingChartCanvas(ref: ElementRef<HTMLCanvasElement> | undefined) {
    if (!ref) {
      this.ratingChart?.destroy();
      this.ratingChart = null;
      this.ratingChartRef = undefined;
      return;
    }
    this.ratingChartRef = ref;
    this.renderRatingChart();
  }
  @ViewChild("heatmapScroller") private heatmapScrollerRef?: ElementRef<HTMLDivElement>;

  private readonly dashboardService = inject(DashboardService);
  private readonly themeService = inject(ThemeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly monthFormatter = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" });
  private readonly fullDateFormatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC"
  });

  readonly theme = this.themeService.theme;

  profile: DashboardProfileResponse | null = null;
  badges: DashboardBadge[] = [];
  activity: SubmissionActivityResponse | null = null;

  heatmapWeeks: HeatmapCell[][] = [];
  monthLabels: HeatmapMonthLabel[] = [];

  loading = true;
  error = "";

  private ratingChart: Chart<"line"> | null = null;
  private ratingChartRef?: ElementRef<HTMLCanvasElement>;

  constructor() {
    effect(() => {
      const currentTheme = this.theme();
      this.applyChartTheme(currentTheme);
    });
  }

  ngOnInit(): void {
    this.loadDashboard();
  }

  ngAfterViewInit(): void {
    this.scrollHeatmapToLatest();
  }

  ngOnDestroy(): void {
    this.ratingChart?.destroy();
    this.ratingChart = null;
  }

  get solvedTotal() {
    return this.profile?.solved.total ?? 0;
  }

  get activityStats() {
    return this.activity?.stats ?? {
      totalActiveDays: 0,
      currentStreak: 0,
      maxStreak: 0,
      totalSubmissions: 0
    };
  }

  getBadgeGlyph(icon: string) {
    const normalized = icon.trim().toLowerCase();
    if (normalized === "award") {
      return "A";
    }
    if (normalized === "medal") {
      return "M";
    }
    if (normalized === "trophy") {
      return "T";
    }
    if (normalized === "flame") {
      return "F";
    }
    if (normalized === "crown") {
      return "C";
    }
    return "B";
  }

  formatRank(rank: number) {
    return `#${rank.toLocaleString("en-US")}`;
  }

  formatCount(value: number) {
    return value.toLocaleString("en-US");
  }

  difficultyPercent(value: number) {
    const total = this.solvedTotal;
    if (!total) {
      return 0;
    }
    return Math.round((value / total) * 100);
  }

  heatmapClass(cell: HeatmapCell) {
    if (!cell.inRange) {
      return "heatmap-cell heatmap-out";
    }
    return `heatmap-cell heatmap-l${cell.level}`;
  }

  heatmapTooltip(cell: HeatmapCell) {
    if (!cell.inRange) {
      return "";
    }
    const formattedDate = this.fullDateFormatter.format(new Date(`${cell.date}T00:00:00Z`));
    const suffix = cell.submissions === 1 ? "submission" : "submissions";
    return `${formattedDate}: ${cell.submissions} ${suffix}`;
  }

  trackByWeek(index: number, week: HeatmapCell[]) {
    return `${index}-${week[0]?.date ?? "week"}`;
  }

  trackByCell(index: number, cell: HeatmapCell) {
    return `${index}-${cell.date}`;
  }

  trackByBadge(_: number, badge: DashboardBadge) {
    return badge.id;
  }

  trackByMonth(_: number, month: HeatmapMonthLabel) {
    return `${month.label}-${month.column}`;
  }

  private loadDashboard() {
    this.loading = true;
    this.error = "";

    this.dashboardService
      .getDashboardBundle()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (bundle) => {
          this.profile = bundle.profile;
          this.badges = bundle.badges;
          this.activity = bundle.activity;
          this.buildHeatmap(bundle.activity.activity);
          this.loading = false;
          this.renderRatingChart();
        },
        error: (error) => {
          this.error = error?.error?.message || "Failed to load dashboard";
          this.loading = false;
        }
      });
  }

  private renderRatingChart() {
    if (!this.profile || !this.ratingChartRef) {
      return;
    }

    const labels = this.profile.ratingHistory.map((point) => point.label);
    const values = this.profile.ratingHistory.map((point) => point.rating);

    if (this.ratingChart) {
      this.ratingChart.destroy();
      this.ratingChart = null;
    }

    const palette = this.getChartPalette(this.theme());
    const config: ChartConfiguration<"line"> = {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            data: values,
            borderColor: palette.line,
            borderWidth: 2.4,
            pointRadius: 2.2,
            pointHoverRadius: 4,
            pointBackgroundColor: palette.point,
            pointBorderColor: palette.point,
            pointHitRadius: 16,
            tension: 0.35,
            fill: true,
            backgroundColor: palette.fill
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            displayColors: false,
            callbacks: {
              label: (ctx) => `Rating ${ctx.parsed.y}`
            }
          }
        },
        scales: {
          x: {
            grid: {
              color: palette.grid,
              drawTicks: false
            },
            ticks: {
              color: palette.tick,
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 6
            }
          },
          y: {
            grid: {
              color: palette.grid,
              drawTicks: false
            },
            ticks: {
              color: palette.tick
            }
          }
        }
      }
    };

    this.ratingChart = new Chart(this.ratingChartRef.nativeElement, config);
  }

  private applyChartTheme(theme: AppTheme) {
    if (!this.ratingChart) {
      return;
    }

    const palette = this.getChartPalette(theme);
    const dataset = this.ratingChart.data.datasets[0];
    dataset.borderColor = palette.line;
    dataset.backgroundColor = palette.fill;
    dataset.pointBackgroundColor = palette.point;
    dataset.pointBorderColor = palette.point;

    const options = this.ratingChart.options as ChartConfiguration<"line">["options"] & {
      scales?: {
        x?: { grid?: { color?: string }; ticks?: { color?: string } };
        y?: { grid?: { color?: string }; ticks?: { color?: string } };
      };
    };

    if (options?.scales?.x?.grid) {
      options.scales.x.grid.color = palette.grid;
    }
    if (options?.scales?.y?.grid) {
      options.scales.y.grid.color = palette.grid;
    }
    if (options?.scales?.x?.ticks) {
      options.scales.x.ticks.color = palette.tick;
    }
    if (options?.scales?.y?.ticks) {
      options.scales.y.ticks.color = palette.tick;
    }

    this.ratingChart.update();
  }

  private buildHeatmap(activity: Array<{ date: string; submissions: number }>) {
    const byDate = new Map(activity.map((entry) => [entry.date, entry.submissions]));

    const today = this.utcToday();
    const start = this.addUtcDays(today, -364);

    const alignedStart = this.addUtcDays(start, -start.getUTCDay());
    const alignedEnd = this.addUtcDays(today, 6 - today.getUTCDay());

    const maxCount = activity.reduce((max, day) => Math.max(max, day.submissions), 0);

    const weeks: HeatmapCell[][] = [];
    const labels: HeatmapMonthLabel[] = [];

    let cursor = alignedStart;
    let weekIndex = 0;
    let lastMonth = -1;

    while (cursor <= alignedEnd) {
      const week: HeatmapCell[] = [];

      for (let day = 0; day < 7; day++) {
        const isoDate = this.toIsoDate(cursor);
        const inRange = cursor >= start && cursor <= today;
        const submissions = inRange ? byDate.get(isoDate) ?? 0 : 0;

        week.push({
          date: isoDate,
          submissions,
          inRange,
          level: inRange ? this.heatLevel(submissions, maxCount) : -1
        });

        cursor = this.addUtcDays(cursor, 1);
      }

      const representative = week.find((cell) => cell.inRange);
      if (representative) {
        const month = new Date(`${representative.date}T00:00:00Z`).getUTCMonth();
        if (month !== lastMonth) {
          labels.push({
            label: this.monthFormatter.format(new Date(`${representative.date}T00:00:00Z`)),
            column: weekIndex
          });
          lastMonth = month;
        }
      }

      weeks.push(week);
      weekIndex++;
    }

    this.heatmapWeeks = weeks;
    this.monthLabels = labels;
    this.scrollHeatmapToLatest();
  }

  private scrollHeatmapToLatest() {
    setTimeout(() => {
      const scroller = this.heatmapScrollerRef?.nativeElement;
      if (!scroller) {
        return;
      }
      scroller.scrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    }, 0);
  }

  private heatLevel(submissions: number, max: number): HeatLevel {
    if (submissions <= 0 || max <= 0) {
      return 0;
    }

    const ratio = submissions / max;
    if (ratio <= 0.25) {
      return 1;
    }
    if (ratio <= 0.5) {
      return 2;
    }
    if (ratio <= 0.75) {
      return 3;
    }
    return 4;
  }

  private getChartPalette(theme: AppTheme) {
    if (theme === "dark") {
      return {
        line: "#f59e0b",
        point: "#fcd34d",
        fill: "rgba(245, 158, 11, 0.16)",
        grid: "rgba(148, 163, 184, 0.22)",
        tick: "#cbd5e1"
      };
    }

    return {
      line: "#f59e0b",
      point: "#b45309",
      fill: "rgba(245, 158, 11, 0.22)",
      grid: "rgba(100, 116, 139, 0.18)",
      tick: "#475569"
    };
  }

  private utcToday() {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  }

  private addUtcDays(date: Date, days: number) {
    return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
  }

  private toIsoDate(date: Date) {
    return date.toISOString().slice(0, 10);
  }
}
